import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { RoutedShell } from '@/app/router/RoutedShell'
import { RequireAuth } from '@/app/router/RequireAuth'
import { AuthLoadingScreen } from '@/components/AuthLoadingScreen'
import { prefetchClubBySlug, prefetchClubsList } from '@/features/clubs/prefetch'
import { STOREFRONT_SCHEDULE_SEGMENT } from '@/features/clubs/storefrontRoutes'
import { prefetchProfileBootstrap } from '@/features/players/prefetch'
import { prefetchProtectedRouteChunks } from '@/lib/router/prefetchRouteChunks'
import { hasSessionHint } from '@/lib/auth/tokenStore'
import { authRoutes } from '@/app/router/routes/authRoutes'
import { guidesRoutes } from '@/app/router/routes/guidesRoutes'
import { helpRoutes } from '@/app/router/routes/helpRoutes'
import { ladderRoutes } from '@/app/router/routes/ladderRoutes'
import { legacyRedirectRoutes } from '@/app/router/routes/legacyRedirectRoutes'
import { marketingRoutes } from '@/app/router/routes/marketingRoutes'
import { paymentReturnRoutes } from '@/app/router/routes/paymentReturnRoutes'
import { playerRoutes } from '@/app/router/routes/playerRoutes'
import { playRoutes } from '@/app/router/routes/playRoutes'
import { tournamentRoutes } from '@/app/router/routes/tournamentRoutes'
import { HomeEntry } from '@/app/router/HomeEntry'

const ProfilePage = lazy(() => import('@/pages/profile/Page').then((m) => ({ default: m.ProfilePage })))
const WorkspacePage = lazy(() => import('@/pages/workspace/Page').then((m) => ({ default: m.WorkspacePage })))
const MyEventsRoute = lazy(() =>
  import('@/pages/workspace/MyEventsRoute').then((m) => ({ default: m.MyEventsRoute })),
)
// The ACCOUNT's subscription (D3.4) — one plan for every club the person owns.
// Its own chunk: /workspace's job is opening a club, and the plan ladder has no
// business on that route's critical path.
const WorkspaceBillingPage = lazy(() =>
  import('@/pages/workspace/billing/Page').then((m) => ({ default: m.WorkspaceBillingPage })),
)
const AdminPage = lazy(() => import('@/pages/admin/Page').then((m) => ({ default: m.AdminPage })))
const ClaimPage = lazy(() => import('@/pages/claim/ClaimPage').then((m) => ({ default: m.ClaimPage })))
const ClubsPage = lazy(() => import('@/pages/clubs/Page').then((m) => ({ default: m.ClubsPage })))
const ClubDetailsPage = lazy(() => import('@/pages/clubs/details/Page').then((m) => ({ default: m.ClubDetailsPage })))
// One membership BILL, by the club's public slug + the bill's own id. Additive: `/clubs/:slug`
// is a two-segment route and cannot match three, so no existing club route changes.
const BillPage = lazy(() => import('@/pages/clubs/bill/BillPage').then((m) => ({ default: m.BillPage })))
const SeriesBillPage = lazy(() => import('@/pages/clubs/bill/SeriesBillPage').then((m) => ({ default: m.SeriesBillPage })))
const OrganizationPage = lazy(() =>
  import('@/pages/organization/Page').then((m) => ({ default: m.OrganizationPage })),
)
const MyBookingsPage = lazy(() =>
  import('@/pages/me/bookings/Page').then((m) => ({ default: m.MyBookingsPage })),
)
const NotFoundPage = lazy(() => import('@/pages/notfound/Page').then((m) => ({ default: m.NotFoundPage })))

function RouteFallback() {
  return <AuthLoadingScreen label="Loading…" />
}

/**
 * Chunk prefetch (§9 lever b): once the session is authenticated, warm the
 * lazy chunks for the common protected destinations so React.lazy resolves
 * instantly on navigation and the RouteFallback ring never fires post-login.
 * Renders nothing; the warm is idempotent and non-blocking.
 */
function RouteChunkPrefetch() {
  const { state } = useAuth()
  useEffect(() => {
    if (state === 'authenticated') prefetchProtectedRouteChunks()
  }, [state])
  return null
}

/**
 * Route-entry kickoff (perf F7): these eager wrappers start the
 * critical fetch during their own render, in parallel with the lazy
 * page chunk, instead of after it executes. startRoutePrefetch is
 * idempotent, so calling it in render is safe (StrictMode included);
 * the page hooks consume the in-flight promise on mount.
 */
function ClubsListEntry() {
  const { state } = useAuth()
  if (state === 'checking') {
    // The HttpOnly refresh cookie is invisible to JS, so 'checking'
    // lasts a full /auth/refresh round trip — for anonymous visitors
    // too, which kept the list prefetch from starting before the route
    // chunk (measured: request start unmoved). The synchronous session
    // hint unblocks the anonymous fast path; browsers with a probable
    // session keep waiting (joined flags are viewer-specific). A stale
    // hint costs at most one extra public request — viewer-keyed
    // entries mean the hook never consumes a mismatched response.
    if (!hasSessionHint()) prefetchClubsList('anon')
  } else {
    prefetchClubsList(state === 'authenticated' ? 'authed' : 'anon')
  }
  return <ClubsPage />
}

function ClubDetailsEntry() {
  const { slug = '' } = useParams<{ slug: string }>()
  const normalizedSlug = slug.trim()
  // Same no-auth-gate semantics as the page's own slug effect.
  if (normalizedSlug) prefetchClubBySlug(normalizedSlug)
  return <ClubDetailsPage />
}

/**
 * A STOREFRONT CLUB'S OWN ADDRESS FOR ITS SCHEDULE AND EVENTS (Club Storefront, brick 2).
 *
 * Renders the very same `OrganizationPage` that `/organization` does — same component,
 * same query string, same `view=public` reading — moved onto a pathname the club owns, so
 * `resolveChrome` dresses it in the club's chrome instead of the platform's. Nothing on
 * the page knows it moved: `useOrganizationRouteParams` reads the query and never the
 * path, which is what makes this a routing change and not a fork of the club's calendar.
 *
 * THE PREFETCH IS WHAT MAKES A COLD OPEN WORK. The chrome resolves the storefront flag
 * from a slug-keyed store that only a public club read fills, and this page performs no
 * such read; without the warm, a pasted link — or a reload in the middle of booking —
 * would fall back to the platform's player chrome. It is the same call `ClubDetailsEntry`
 * makes and shares its cache entry, so arriving from the club page costs no request.
 */
function ClubStorefrontScheduleEntry() {
  const { slug = '' } = useParams<{ slug: string }>()
  const normalizedSlug = slug.trim()
  if (normalizedSlug) prefetchClubBySlug(normalizedSlug)
  return <OrganizationPage />
}

// /profile's critical read is the player bootstrap, keyed by the signed-in
// user's linked playerId — which the auth context already carries once
// authenticated. Kick it off here (recent_limit=5, matching the page's own
// usePlayerView load) so it runs in parallel with the lazy ProfilePage chunk;
// usePlayerView consumes the in-flight promise on mount.
function ProfileEntry() {
  const { state, me } = useAuth()
  if (state === 'authenticated') {
    const playerId = String(me?.player?.playerId || '').trim()
    if (playerId) prefetchProfileBootstrap(playerId, 5)
  }
  return <ProfilePage />
}

/**
 * The PAYMENT RETURN LEG (D3.4). Stripe and PayPal bounce the browser to
 * `/organization?orgId=<anchor>&tab=billing&checkout=<outcome>` — a URL baked
 * into `backend/src/modules/billing/{checkout,paypalCheckout}.mjs` AND into every
 * PayPal subscription already created against it, so it cannot be changed from
 * here and must not be broken for the ones in flight.
 *
 * Since 2026-08-07 that tab is a read-only pointer, which would land a customer
 * who has just paid real money on a page that says "manage this somewhere else".
 * So the `checkout` marker — which only a processor return carries — is caught
 * BEFORE the org page's chunk is even fetched and forwarded to the account page
 * that owns the subscription, keeping the outcome and dropping the anchor orgId
 * (an implementation detail of org-keyed billing storage, not something the payer
 * needs). `replace` so Back returns to the processor's origin, never to a landing
 * that would re-forward.
 */
function OrganizationEntry() {
  const [searchParams] = useSearchParams()
  const outcome = String(searchParams.get('checkout') || '').trim()
  if (outcome) {
    return <Navigate to={`/workspace/billing?checkout=${encodeURIComponent(outcome)}`} replace />
  }
  return <OrganizationPage />
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <RoutedShell>
        <RouteChunkPrefetch />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomeEntry />} />
            {authRoutes()}
            {tournamentRoutes()}
            {ladderRoutes()}
            {legacyRedirectRoutes()}
            {/* The Get Started chooser now lives on the personal home (home split,
                2026-09-23). The address stays: the login-landing rule still sends a
                0-club account here, and links and bookmarks exist. */}
            <Route path="/start" element={<Navigate to="/" replace />} />
            {playRoutes()}
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfileEntry />
                </RequireAuth>
              }
            />
            {/* Legacy alias — the profile-merge request feature was removed
                (2026-08-04). Old links (and the claim-failure page before its
                copy change) still point here, so keep them landing on Profile. */}
            <Route path="/profile/merge-request" element={<Navigate to="/profile" replace />} />
            <Route
              path="/claim/:tokenId"
              element={
                <RequireAuth>
                  <ClaimPage />
                </RequireAuth>
              }
            />
            <Route path="/clubs" element={<ClubsListEntry />} />
            <Route path="/clubs/:slug" element={<ClubDetailsEntry />} />
            {/* The club's own schedule + events board — the storefront's till. The path
                segment comes from the same constant `resolveChrome` admits, so the route
                that exists and the pathname that gets the club's chrome cannot drift. */}
            <Route
              path={`/clubs/:slug/${STOREFRONT_SCHEDULE_SEGMENT}`}
              element={<ClubStorefrontScheduleEntry />}
            />
            {/* Publicly reachable, but the bill's CONTENTS are not: a signed-out visitor
                gets a wall that names the club and nothing else (there is no
                unauthenticated bill read anywhere in the backend). */}
            <Route path="/clubs/:slug/bill/series/:seriesId" element={<SeriesBillPage />} />
            <Route path="/clubs/:slug/bill/:cycleId" element={<BillPage />} />
            {playerRoutes()}
            <Route
              path="/workspace"
              element={
                <RequireAuth>
                  <WorkspacePage />
                </RequireAuth>
              }
            />
            {/* The ONE address a subscription is bought and managed at (D3.4).
                `/workspace` is an exact path, so the two never compete for a
                match and the order between them carries no meaning. */}
            <Route
              path="/workspace/billing"
              element={
                <RequireAuth>
                  <WorkspaceBillingPage />
                </RequireAuth>
              }
            />
            <Route
              path="/my-events"
              element={
                <RequireAuth>
                  <MyEventsRoute />
                </RequireAuth>
              }
            />
            {/* `/me` has never been a page of its own, yet card returns and links name it (a
                club package's Finish payment, the card return page's `See my passes`). It lands
                on Profile › Lessons, where the passes and the booked sessions are. */}
            <Route path="/me" element={<Navigate to="/profile?tab=lessons" replace />} />
            <Route
              path="/me/bookings"
              element={
                <RequireAuth>
                  <MyBookingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/organization"
              element={<OrganizationEntry />}
            />
            <Route
              path="/admin"
              element={
                /* The PLATFORM console — entitlements, credits, AI pricing, every
                   club's row. The server gates it in three places, so there is no
                   data hole; the router had none, so anyone signed in could DRAW it
                   and read an admin's frame before every control inside it refused. */
                <RequireAuth requireRole="super_admin">
                  <AdminPage />
                </RequireAuth>
              }
            />
            {paymentReturnRoutes()}
            {marketingRoutes()}
            {guidesRoutes()}
            {helpRoutes()}
            <Route path="/health" element={<Navigate to="/" replace />} />
            {/* Catch-all: render the real 404 page (noindex) instead of
                teleporting to `/`. The old `<Navigate to="/">` served HOME
                content under every bogus URL, and Googlebot executes JS — so
                junk URLs were indexable duplicates of the home page. Every
                legacy alias above still redirects; only the true miss lands
                here. NOTE: the HTTP status is still 200 (CloudFront rewrites
                403/404 → index.html); see the page's own comment. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </RoutedShell>
    </BrowserRouter>
  )
}
